import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, ChevronRight } from 'lucide-react';
import { MobileButton } from '../components/MobileUIKit';
import '../styles/mobile.css';

export const DriverDeliverySuccess = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [rating, setRating] = useState(0);

  useEffect(() => {
    // Trigger animation after mount
    setTimeout(() => setShowContent(true), 100);
    
    // Auto-redirect after 5 seconds if no action
    const timer = setTimeout(() => {
      navigate('/driver');
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [navigate]);

  const earnings = 12.50;
  const tip = 2.50;

  return (
    <div 
      className="mobile-app min-h-screen flex flex-col items-center justify-center px-6"
      style={{ 
        background: 'linear-gradient(180deg, var(--mobile-bg-primary) 0%, #0a1f0a 100%)'
      }}
    >
      {/* Confetti Effect (CSS animation placeholder) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-10%`,
              background: ['#FFD700', '#E53935', '#4CAF50', '#42A5F5'][i % 4],
              animation: `fall ${2 + Math.random() * 2}s linear ${Math.random() * 2}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Success Content */}
      <div 
        className={`text-center transition-all duration-500 ${
          showContent ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
        }`}
      >
        {/* Success Icon */}
        <div className="relative mb-6">
          <div 
            className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
            style={{ 
              background: 'var(--mobile-success)',
              boxShadow: '0 0 60px rgba(76, 175, 80, 0.5)'
            }}
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          
          {/* Animated rings */}
          <div 
            className="absolute inset-0 w-24 h-24 mx-auto rounded-full border-2"
            style={{ 
              borderColor: 'var(--mobile-success)',
              animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite'
            }}
          />
        </div>

        {/* Title */}
        <h1 
          className="mobile-text-hero mb-2"
          style={{ color: 'var(--mobile-text-primary)' }}
        >
          Delivered! 🎉
        </h1>
        
        <p className="mobile-text-body mb-8" style={{ color: 'var(--mobile-text-secondary)' }}>
          Great job! You've completed the delivery.
        </p>

        {/* Earnings Display */}
        <div 
          className="p-6 rounded-2xl mb-6"
          style={{ 
            background: 'rgba(0, 200, 83, 0.1)',
            border: '1px solid rgba(0, 200, 83, 0.3)'
          }}
        >
          <p className="mobile-text-tiny mb-1" style={{ color: 'var(--mobile-text-secondary)' }}>
            You earned
          </p>
          <p 
            className="text-5xl font-bold"
            style={{ 
              color: 'var(--mobile-earnings)',
              textShadow: '0 0 30px rgba(0, 200, 83, 0.5)'
            }}
          >
            ${(earnings + tip).toFixed(2)}
          </p>
          {tip > 0 && (
            <p className="mobile-text-caption mt-2" style={{ color: 'var(--mobile-tip)' }}>
              Including ${tip.toFixed(2)} tip! 💰
            </p>
          )}
        </div>

        {/* Rate Customer */}
        <div className="mb-8">
          <p className="mobile-text-caption mb-3" style={{ color: 'var(--mobile-text-secondary)' }}>
            Rate your experience with this customer
          </p>
          <div className="mobile-flex mobile-justify-center mobile-gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="p-1 transition-transform active:scale-90"
              >
                <Star
                  className="w-10 h-10"
                  fill={star <= rating ? '#FFD700' : 'transparent'}
                  stroke={star <= rating ? '#FFD700' : 'var(--mobile-text-disabled)'}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 w-full max-w-xs mx-auto">
          <MobileButton
            variant="primary"
            fullWidth
            onClick={() => navigate('/driver')}
          >
            Continue Delivering
          </MobileButton>
          
          <MobileButton
            variant="ghost"
            fullWidth
            onClick={() => navigate('/driver/earnings')}
          >
            View Earnings
            <ChevronRight className="w-4 h-4 ml-1" />
          </MobileButton>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(720deg);
          }
        }
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default DriverDeliverySuccess;
